// PB-4922 (AC-15). The two PUBLIC invitation endpoints.
//
// A separate module from `invites-api.ts` on purpose. That one is the ADMIN
// surface: it runs behind the auth guard, sends the CSRF header, and its errors
// are shaped for someone who is already signed in. These two calls are made by a
// visitor with no session and no account, so they share none of that — and
// putting them in the same module would invite a future edit to give them the
// admin client's redirect-on-401 behaviour, which for an invitee means being
// bounced to a login they cannot complete.
//
// THE TOKEN TRAVELS IN THE REQUEST BODY, never in the path. `services/auth`
// sanitises exactly one URL path segment for telemetry (`/delegated-sessions/`),
// so a token in the path would land verbatim in `url.path` and `http.target` on
// every exported span. The body is not logged: pino is configured with no body
// serializer, and `inviteToken` additionally carries the four redaction entries
// the service applies to every other show-once credential.

/** What the preview may disclose, and it is deliberately almost nothing. */
export interface InvitePreview {
  /** Tenant display name, so the page is not baffling. */
  readonly workspaceName: string;
  readonly expiresAt: string;
}

/**
 * Every refusal, without saying which one it was.
 *
 * The server returns one identical 404 for an unknown token, an expired one, a
 * revoked one, one already accepted, one belonging to another workspace, one
 * pinned to the corporate route, and a mismatched confirmation address. The
 * client keeps that collapsed rather than trying to be helpful: anything that
 * distinguishes them here would reconstruct, in the browser, the oracle the
 * server was careful not to give.
 */
export class InviteNotRedeemableError extends Error {
  constructor() {
    super("This invitation link is not valid.");
    this.name = "InviteNotRedeemableError";
  }
}

/** The request was refused for a reason that is NOT about the token. */
export class InviteRequestFailedError extends Error {
  constructor(readonly status: number) {
    super("The invitation service could not be reached.");
    this.name = "InviteRequestFailedError";
  }
}

/**
 * The server's single refusal, and the ONLY 404 that means "this link is dead".
 *
 * A bare `status === 404` is not enough, and the difference is not academic. A
 * 404 also arrives when the ROUTE is absent — an auth service deployed behind
 * the shell, a gateway that has not learned the path yet, a proxy pointed at the
 * wrong upstream. Those are transient and recoverable; the invitee should be
 * told to try again, not handed the terminal screen that says their link is
 * spent and to go ask for another one.
 *
 * Argo makes this a real rollout state rather than a hypothetical: the shell and
 * auth are separate deployments in separate waves, so there is a window in which
 * the page exists and the endpoint does not. Without this check every invitee
 * who clicks during that window is told, permanently and with no retry, that a
 * perfectly good link is invalid.
 *
 * Found by clicking a real link against a shell whose proxy pointed at an auth
 * service that predated these routes: the page said "not valid" for an
 * invitation that was pending, local and unexpired.
 */
async function isNotRedeemable(response: Response): Promise<boolean> {
  if (response.status !== 404) return false;
  try {
    const body: unknown = await response.clone().json();
    return (
      typeof body === "object" &&
      body !== null &&
      (body as { code?: unknown }).code === "invitation_not_redeemable"
    );
  } catch {
    /* A 404 with no JSON body is not this service refusing a token — it is
       something in front of it. Treat it as unreachable. */
    return false;
  }
}

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    /* No `credentials: "include"`. The invitee has no session, and sending one
       if a stale cookie happens to exist would make the request behave
       differently for a signed-in visitor than for the anonymous one this page
       is built for. */
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * Read-only. Safe for a mail scanner to fetch, which matters: Defender Safe
 * Links, Proofpoint and Mimecast all dereference URLs found in inbound mail
 * before the human ever clicks, so anything that consumed or counted the
 * invitation here would be spent by the scanner and the real invitee would find
 * a dead link every time.
 */
export async function previewInvite(
  inviteToken: string,
): Promise<InvitePreview> {
  const response = await post("/api/auth/invitations/preview", { inviteToken });
  if (await isNotRedeemable(response)) throw new InviteNotRedeemableError();
  if (!response.ok) throw new InviteRequestFailedError(response.status);
  return (await response.json()) as InvitePreview;
}

/**
 * Redeem it. Resolves on success and the caller then drives the ORDINARY login
 * flow — no session is minted here, and Keycloak collects the password on its
 * own screen.
 */
export interface InviteAcceptance {
  readonly setPasswordUrl: string;
}

export async function acceptInvite(
  inviteToken: string,
  confirmedEmail: string,
): Promise<InviteAcceptance> {
  const response = await post("/api/auth/invitations/accept", {
    inviteToken,
    confirmedEmail,
  });
  if (await isNotRedeemable(response)) throw new InviteNotRedeemableError();
  if (!response.ok) throw new InviteRequestFailedError(response.status);
  const body = (await response.json().catch(() => null)) as {
    setPasswordUrl?: unknown;
  } | null;
  if (!body || typeof body.setPasswordUrl !== "string") {
    throw new InviteRequestFailedError(response.status);
  }
  return { setPasswordUrl: body.setPasswordUrl };
}
